import React, { useEffect, useState } from "react";
import {
  Drawer,
  Button,
  Input,
  InputNumber,
  message,
  Checkbox,
  Divider,
  Empty,
  Spin,
} from "antd";
import { SearchOutlined, ShoppingCartOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Popconfirm, Form, Space } from "antd";
import { useCart } from "../../../Context";
import { TemporaryItemProduct } from "../../../Enums/TemporaryItem";
import {
  useServicesQuery,
  useServiceCreateMutation,
  useServiceUpdateMutation,
  useServiceDeleteMutation,
} from "../../../Services/service_management";
import { Service } from "../../../Types";
import { drawerTitle, drawerFooter, DRAWER_FORM_WIDTH } from "../../../components";

interface ServicesDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const ServicesDrawer: React.FC<ServicesDrawerProps> = ({ open, onClose }) => {
  const { addToCart } = useCart();

  const [search, setSearch] = useState("");
  const { data: services = [], isLoading } = useServicesQuery(search, { enabled: open });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [totalCost, setTotalCost] = useState<number>(0);

  const [crudForm] = Form.useForm();
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [isCrudOpen, setIsCrudOpen] = useState(false);

  const createMutation = useServiceCreateMutation();
  const updateMutation = useServiceUpdateMutation();
  const deleteMutation = useServiceDeleteMutation();

  const selectedServices = services.filter((s) => selectedIds.includes(s.id));

  useEffect(() => {
    const sumPrice = selectedServices.reduce(
      (acc, s) => acc + Number(s.default_price || 0),
      0
    );
    const sumCost = selectedServices.reduce(
      (acc, s) => acc + Number(s.default_cost || 0),
      0
    );
    setTotalPrice(sumPrice);
    setTotalCost(sumCost);
  }, [selectedIds, services]);

  const handleToggle = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  const handleAddToCart = () => {
    if (selectedServices.length === 0) {
      message.warning("Please select at least one service.");
      return;
    }
    if (totalPrice < 0) {
      message.error("Total price must be 0 or greater.");
      return;
    }

    const note = selectedServices.map((s) => `• ${s.name}`).join("\n");

    addToCart(
      {
        id: TemporaryItemProduct.PRODUCT,
        title: "Service Item",
        image: "",
        is_tempory_item: true,
      },
      {
        batch_no: `${TemporaryItemProduct.STOCK}-service-${Date.now()}`,
        cost_price: totalCost,
      },
      totalPrice,
      1,
      { is_tempory_item: true },
      totalCost,
      note
    );

    message.success("Service Item added to cart.");
    setSelectedIds([]);
    setTotalPrice(0);
    setTotalCost(0);
    onClose();
  };

  const openAddModal = () => {
    setEditingService(null);
    crudForm.resetFields();
    crudForm.setFieldsValue({ default_cost: 0, default_price: 0 });
    setIsCrudOpen(true);
  };

  const openEditModal = (service: Service) => {
    setEditingService(service);
    crudForm.setFieldsValue({
      name: service.name,
      default_cost: service.default_cost,
      default_price: service.default_price,
    });
    setIsCrudOpen(true);
  };

  const handleSaveTemplate = (values: any) => {
    const payload = {
      name: values.name.trim(),
      default_cost: Number(values.default_cost ?? 0),
      default_price: Number(values.default_price ?? 0),
    };

    if (editingService) {
      updateMutation.mutate(
        { ...payload, id: editingService.id },
        {
          onSuccess: (res) => {
            if (res.success) {
              message.success("Service updated.");
              setIsCrudOpen(false);
              setEditingService(null);
              crudForm.resetFields();
            } else {
              message.error(res.message || "Failed to update.");
            }
          },
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (res) => {
          if (res.success) {
            message.success("Service added.");
            setIsCrudOpen(false);
            crudForm.resetFields();
          } else {
            message.error(res.message || "Failed to add service.");
          }
        },
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: (res) => {
        if (res.success) {
          message.success("Service deleted.");
          setSelectedIds((prev) => prev.filter((x) => x !== id));
        } else {
          message.error(res.message || "Failed to delete.");
        }
      },
    });
  };

  return (
    <Drawer
      title={drawerTitle("Services")}
      open={open}
      onClose={() => {
        setSelectedIds([]);
        setTotalPrice(0);
        setTotalCost(0);
        onClose();
      }}
      width={DRAWER_FORM_WIDTH}
      destroyOnClose
      footer={
        selectedServices.length > 0 ? (
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 flex flex-col gap-3">
            {/* Selected services preview (note) */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Cart Note
              </p>
              <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 leading-relaxed whitespace-pre-line max-h-24 overflow-y-auto">
                {selectedServices.map((s) => `• ${s.name}`).join("\n")}
              </div>
            </div>

            {/* Editable cost and price */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Cost Price (Rs.)
                </p>
                <InputNumber
                  className="w-full"
                  min={0}
                  precision={2}
                  value={totalCost}
                  onChange={(val) => setTotalCost(Number(val ?? 0))}
                  size="large"
                />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                  Selling Price (Rs.)
                </p>
                <InputNumber
                  className="w-full"
                  min={0}
                  precision={2}
                  value={totalPrice}
                  onChange={(val) => setTotalPrice(Number(val ?? 0))}
                  size="large"
                />
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              icon={<ShoppingCartOutlined />}
              onClick={handleAddToCart}
              className="w-full bg-emerald-600 hover:bg-emerald-700 border-none font-semibold text-xs tracking-wide h-11"
            >
              ADD SERVICE ITEM TO CART
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col h-full gap-y-4">
        {/* Search + Add New */}
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search services..."
            prefix={<SearchOutlined className="text-gray-400" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            className="flex-1"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAddModal}
            className="bg-emerald-600 hover:bg-emerald-700 border-none shrink-0"
          >
            Add New
          </Button>
        </div>

        {/* Service List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Spin />
            </div>
          ) : services.length === 0 ? (
            <Empty description="No services found." />
          ) : (
            <div className="flex flex-col gap-1">
              {services.map((service) => {
                const isChecked = selectedIds.includes(service.id);
                return (
                  <div
                    key={service.id}
                    onClick={() => handleToggle(service.id, !isChecked)}
                    className={`flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                      isChecked
                        ? "bg-emerald-50 border-emerald-400 shadow-sm"
                        : "bg-white border-gray-100 hover:border-gray-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggle(service.id, e.target.checked);
                        }}
                      />
                      <div>
                        <p className="text-sm font-semibold text-gray-800 leading-tight">
                          {service.name}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Default: Rs.{" "}
                          {Number(service.default_price || 0).toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* Edit / Delete */}
                    <Space
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined className="text-blue-400" />}
                        onClick={() => openEditModal(service)}
                      />
                      <Popconfirm
                        title="Delete this service?"
                        onConfirm={() => handleDelete(service.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </Space>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Add / Edit Template Drawer */}
      <Drawer
        title={drawerTitle(editingService ? "Edit Service" : "Add Service")}
        open={isCrudOpen}
        onClose={() => setIsCrudOpen(false)}
        width={DRAWER_FORM_WIDTH}
        destroyOnClose
        footer={drawerFooter({
          actions: [
            {
              label: "Cancel",
              onClick: () => setIsCrudOpen(false),
            },
            {
              label: editingService ? "Update Service" : "Save Service",
              type: "primary",
              loading: createMutation.isPending || updateMutation.isPending,
              onClick: () => crudForm.submit(),
            },
          ],
        })}
      >
        <Form form={crudForm} layout="vertical" onFinish={handleSaveTemplate}>
          <Form.Item
            name="name"
            label="Service Name"
            rules={[{ required: true, message: "Name is required." }]}
          >
            <Input placeholder="e.g. Engine Oil Change" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-3">
            <Form.Item
              name="default_cost"
              label="Default Cost (Rs.)"
              rules={[{ required: true, message: "Required." }]}
            >
              <InputNumber className="w-full" min={0} precision={2} />
            </Form.Item>
            <Form.Item
              name="default_price"
              label="Default Price (Rs.)"
              rules={[{ required: true, message: "Required." }]}
            >
              <InputNumber className="w-full" min={0} precision={2} />
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </Drawer>
  );
};
